import React, { useEffect } from 'react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { StatCard } from '../components/rogerdoc/StatCard';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Alert, AlertDescription } from '@roger/ui-components';
import { Users, FileText, Clock, Loader2, AlertTriangle } from 'lucide-react';

const RogerDocOverview: React.FC = () => {
  const {
    hasPermission,
    usageStats,
    activeUsers,
    visitStats,
    timeSavings,
    topSavingsClinicians,
    activeUsersLoading,
    visitStatsLoading,
    timeSavingsLoading,
    topSavingsLoading,
    usageStatsLoading,
    fetchAllOverviewData,
  } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
      usageStats: state.usageStats,
      activeUsers: state.activeUsers,
      visitStats: state.visitStats,
      timeSavings: state.timeSavings,
      topSavingsClinicians: state.topSavingsClinicians,
      activeUsersLoading: state.activeUsersLoading,
      visitStatsLoading: state.visitStatsLoading,
      timeSavingsLoading: state.timeSavingsLoading,
      topSavingsLoading: state.topSavingsLoading,
      usageStatsLoading: state.usageStatsLoading,
      fetchAllOverviewData: state.fetchAllOverviewData,
    }))
  );

  // Check if user has permission to access RogerDoc Dashboard
  if (!hasPermission('rogerdoc-dashboard:manage')) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertTriangle className='h-4 w-4 text-red-600' />
          <AlertDescription className='text-red-800'>
            You don't have permission to access RogerDoc Dashboard features.
            Please contact your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  useEffect(() => {
    fetchAllOverviewData();
  }, []);

  const renderValue = (value: React.ReactNode, loading: boolean) => {
    if (loading) {
      return <Loader2 className='h-5 w-5 animate-spin' />;
    }
    return value ?? '-';
  };

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-3xl font-bold'>Overview</h1>
        <p className='text-muted-foreground mt-1'>
          RogerDoc Dashboard Analytics
        </p>
      </div>

      {/* Stats Grid */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
        <StatCard
          Icon={Users}
          value={renderValue(usageStats?.length, usageStatsLoading)}
          label='Onboarded Users'
        />
        <StatCard
          Icon={Users}
          value={renderValue(activeUsers?.length, activeUsersLoading)}
          label='Active Users (last 30 days)'
        />
        <StatCard
          Icon={FileText}
          value={renderValue(visitStats?.OASIS, visitStatsLoading)}
          label='OASIS Visits'
        />
        <StatCard
          Icon={FileText}
          value={renderValue(visitStats?.ROUTINE, visitStatsLoading)}
          label='Routine Visits'
        />
        <StatCard
          Icon={Clock}
          value={renderValue(
            timeSavings?.avg_oasis_time_saving
              ? parseFloat(
                  timeSavings.avg_oasis_time_saving.toString()
                ).toFixed(1)
              : '-',
            timeSavingsLoading
          )}
          label='Average Time Savings - OASIS Visits (in min)'
        />
        <StatCard
          Icon={Clock}
          value={renderValue(
            timeSavings?.avg_routine_time_saving
              ? parseFloat(
                  timeSavings.avg_routine_time_saving.toString()
                ).toFixed(1)
              : '-',
            timeSavingsLoading
          )}
          label='Average Time Savings - Routine Visits (in min)'
        />
      </div>

      {/* Top 10 Clinicians Table */}
      <Card>
        <CardHeader>
          <CardTitle>Top 10 clinicians with the highest time savings</CardTitle>
        </CardHeader>
        <CardContent>
          {topSavingsLoading ? (
            <div className='flex items-center justify-center py-8'>
              <Loader2 className='h-8 w-8 animate-spin text-primary' />
            </div>
          ) : topSavingsClinicians && topSavingsClinicians.length > 0 ? (
            <Table className='text-base'>
              <TableHeader>
                <TableRow>
                  <TableHead className='text-base font-semibold'>
                    Rank
                  </TableHead>
                  <TableHead className='text-base font-semibold'>
                    Name
                  </TableHead>
                  <TableHead className='text-base font-semibold text-right'>
                    Total Saved (min)
                  </TableHead>
                  <TableHead className='text-base font-semibold text-right'>
                    Avg OASIS Savings (min)
                  </TableHead>
                  <TableHead className='text-base font-semibold text-right'>
                    Avg Routine Savings (min)
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topSavingsClinicians.map((clinician, index) => (
                  <TableRow key={index}>
                    <TableCell className='text-base font-medium'>
                      {index + 1}
                    </TableCell>
                    <TableCell className='text-base'>
                      {clinician.clinician_name}
                    </TableCell>
                    <TableCell className='text-base text-right'>
                      {parseFloat(
                        clinician?.total_time_saved?.toString()
                      ).toFixed(1)}
                    </TableCell>
                    <TableCell className='text-base text-right'>
                      {parseFloat(
                        clinician?.avg_oasis_time_saving?.toString()
                      ).toFixed(1)}
                    </TableCell>
                    <TableCell className='text-base text-right'>
                      {parseFloat(
                        clinician?.avg_routine_time_saving?.toString()
                      ).toFixed(1)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className='text-center py-8 text-base text-muted-foreground'>
              No data available
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RogerDocOverview;
